import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { Building, Calendar, FileText, CheckCircle, Clock, AlertCircle, Settings, ArrowUpDown } from 'lucide-react';
import './MaintenanceStart.css';
import { Building as BuildingType } from '../../../types/protocols';
import MaintenanceTemplatesEditor from '../TemplatesEditor/MaintenanceTemplatesEditor';
import { Helmet } from 'react-helmet-async';

interface BuildingStatus {
    hasActive: boolean;
    hasProposal: boolean;
    hasDraft: boolean;
    proposalStatus?: 'pending' | 'approved' | 'rejected';
    lastUpdated?: string;
}

interface BuildingWithStatus extends BuildingType {
    status: BuildingStatus;
}

const MaintenanceStart: React.FC = () => {
    const navigate = useNavigate();
    const [buildings, setBuildings] = useState<BuildingWithStatus[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<'buildings' | 'templates'>('buildings');
    const [sortField, setSortField] = useState<'name' | 'lastUpdated'>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [selectedBuildingKey, setSelectedBuildingKey] = useState<string>('');

    const db = getDatabase();

    // Load buildings and their maintenance status
    const loadBuildingsWithStatus = async (): Promise<void> => {
        try {
            setLoading(true);

            // Load buildings summary
            const buildingsSnapshot = await get(ref(db, 'buildingsSummary'));
            if (!buildingsSnapshot.exists()) {
                setBuildings([]);
                return;
            }

            const buildingsData = buildingsSnapshot.val() as Record<string, any>;
            const buildingKeys = Object.keys(buildingsData);

            // Load maintenance status for all buildings in parallel
            const statusPromises = buildingKeys.map(async (buildingKey) => {
                const [activeSnapshot, proposedSnapshot, draftSnapshot] = await Promise.all([
                    get(ref(db, `maintenance/active/${buildingKey}`)),
                    get(ref(db, `maintenance/proposed/${buildingKey}`)),
                    get(ref(db, `maintenance/drafts/${buildingKey}`))
                ]);

                const status: BuildingStatus = {
                    hasActive: activeSnapshot.exists(),
                    hasProposal: proposedSnapshot.exists(),
                    hasDraft: draftSnapshot.exists()
                };

                // Get proposal status if exists
                if (proposedSnapshot.exists()) {
                    const proposedData = proposedSnapshot.val();
                    status.proposalStatus = proposedData.submission?.status || 'pending';
                    status.lastUpdated = proposedData.submission?.submittedAt || proposedData.metadata?.savedAt;
                } else if (draftSnapshot.exists()) {
                    status.lastUpdated = draftSnapshot.val().metadata?.savedAt;
                } else if (activeSnapshot.exists()) {
                    status.lastUpdated = activeSnapshot.val().metadata?.savedAt;
                }

                return {
                    key: buildingKey,
                    summary: buildingsData[buildingKey],
                    model: { key: buildingKey }, // We don't need the full model for this view
                    status
                } as BuildingWithStatus;
            });

            const buildingsWithStatus = await Promise.all(statusPromises);
            
            // Sort by name by default
            buildingsWithStatus.sort((a, b) => {
                const nameA = a.summary?.name || 'Namnlös byggnad';
                const nameB = b.summary?.name || 'Namnlös byggnad';
                return nameA.localeCompare(nameB, 'sv-SE');
            });
            
            setBuildings(buildingsWithStatus);

        } catch (error) {
            console.error('Error loading buildings with status:', error);
        } finally {
            setLoading(false);
        }
    };

    // Navigate to specific maintenance page
    const navigateToMaintenance = (buildingKey: string, type: 'active' | 'proposal' | 'draft') => {
        switch (type) {
            case 'active':
                navigate(`/maintenanceActive/${buildingKey}`);
                break;
            case 'proposal':
                navigate(`/maintenanceReview/${buildingKey}`);
                break;
            case 'draft':
                navigate(`/maintenanceDrafts/${buildingKey}`);
                break;
        }
    };

    // Show create draft modal
    const handleCreateDraft = (buildingKey: string) => {
        setSelectedBuildingKey(buildingKey);
        setShowCreateModal(true);
    };

    // Confirm create draft
    const handleConfirmCreateDraft = () => {
        setShowCreateModal(false);
        navigate(`/maintenanceDrafts/${selectedBuildingKey}`);
    };

    // Cancel create draft
    const handleCancelCreateDraft = () => {
        setShowCreateModal(false);
        setSelectedBuildingKey('');
    };

    // Handle sorting
    const handleSort = (field: 'name' | 'lastUpdated') => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Get sorted buildings
    const getSortedBuildings = (): BuildingWithStatus[] => {
        return [...buildings].sort((a, b) => {
            let comparison = 0;
            
            if (sortField === 'name') {
                const nameA = a.summary?.name || 'Namnlös byggnad';
                const nameB = b.summary?.name || 'Namnlös byggnad';
                comparison = nameA.localeCompare(nameB, 'sv-SE');
            } else if (sortField === 'lastUpdated') {
                const dateA = a.status.lastUpdated ? new Date(a.status.lastUpdated).getTime() : 0;
                const dateB = b.status.lastUpdated ? new Date(b.status.lastUpdated).getTime() : 0;
                comparison = dateA - dateB;
            }
            
            return sortDirection === 'asc' ? comparison : -comparison;
        });
    };

    // Get status button variant and text
    const getStatusButtonInfo = (type: 'active' | 'proposal' | 'draft', status?: BuildingStatus) => {
        switch (type) {
            case 'active':
                return {
                    variant: 'success',
                    text: 'Aktiv plan',
                    icon: <CheckCircle size={16} className="me-1" />
                };
            case 'proposal':
                if (status?.proposalStatus === 'pending') {
                    return {
                        variant: 'warning',
                        text: 'Väntar på granskning',
                        icon: <Clock size={16} className="me-1" />
                    };
                } else if (status?.proposalStatus === 'approved') {
                    return {
                        variant: 'success',
                        text: 'Godkänd',
                        icon: <CheckCircle size={16} className="me-1" />
                    };
                } else if (status?.proposalStatus === 'rejected') {
                    return {
                        variant: 'danger',
                        text: 'Avslagen',
                        icon: <AlertCircle size={16} className="me-1" />
                    };
                }
                return {
                    variant: 'secondary',
                    text: 'Inget förslag',
                    icon: null
                };
            case 'draft':
                return {
                    variant: 'info',
                    text: 'Utkast',
                    icon: <FileText size={16} className="me-1" />
                };
            default:
                return {
                    variant: 'secondary',
                    text: 'Ingen',
                    icon: null
                };
        }
    };

    // Format last updated date
    const formatLastUpdated = (dateString?: string): string => {
        if (!dateString) return 'Aldrig';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('sv-SE');
        } catch {
            return 'Okänt datum';
        }
    };

    useEffect(() => {
        loadBuildingsWithStatus();
    }, []);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <h5 className="text-muted">Laddar byggnader...</h5>
                </div>
            </div>
        );
    }

    return (
        <div className="maintenance-start">
            <Helmet>
                <title>Underhållshantering - Molnfastigheter</title>
            </Helmet>
            {/* Header */}
            <div className="header-section">
                <div className="header-content">
                    <h1 className="display-5 fw-bold text-white mb-2">
                        Underhållshantering
                    </h1>
                    <p className="lead text-white-50 mb-0">
                        Hantera underhållsplaner för alla byggnader
                    </p>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="navigation-section">
                <div className="nav-tabs-container">
                    <ul className="nav nav-tabs" role="tablist">
                        <li className="nav-item" role="presentation">
                            <button
                                className={`nav-link ${activeTab === 'buildings' ? 'active' : ''}`}
                                onClick={() => setActiveTab('buildings')}
                                type="button"
                            >
                                <Building size={20} className="me-2" />
                                Byggnader
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button
                                className={`nav-link ${activeTab === 'templates' ? 'active' : ''}`}
                                onClick={() => setActiveTab('templates')}
                                type="button"
                            >
                                <Settings size={20} className="me-2" />
                                Underhållsmallar
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Content */}
            <div className="content-section">
                {activeTab === 'buildings' ? (
                    <div className="buildings-tab">
                        {buildings.length === 0 ? (
                            <div className="empty-state">
                                <Building size={64} className="text-muted mb-3" />
                                <h4 className="text-muted">Inga byggnader hittades</h4>
                                <p className="text-muted">Det finns inga byggnader att visa för tillfället.</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover buildings-table">
                                    <thead>
                                        <tr>
                                            <th 
                                                className="sortable-header"
                                                onClick={() => handleSort('name')}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <span>Byggnad</span>
                                                    <ArrowUpDown size={16} className="ms-2" />
                                                    {sortField === 'name' && (
                                                        <span className="sort-indicator ms-1">
                                                            {sortDirection === 'asc' ? '↑' : '↓'}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                            <th 
                                                className="sortable-header"
                                                onClick={() => handleSort('lastUpdated')}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <span>Senast uppdaterad</span>
                                                    <ArrowUpDown size={16} className="ms-2" />
                                                    {sortField === 'lastUpdated' && (
                                                        <span className="sort-indicator ms-1">
                                                            {sortDirection === 'asc' ? '↑' : '↓'}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                            <th>Aktiv plan</th>
                                            <th>Förslag</th>
                                            <th>Utkast</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getSortedBuildings().map((building) => (
                                            <tr key={building.key} className="building-row">
                                                <td>
                                                    <div className="building-name-cell">
                                                        <strong>{building.summary?.name || 'Namnlös byggnad'}</strong>
                                                        <small className="text-muted d-block">
                                                            {building.key}
                                                        </small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="last-updated">
                                                        {formatLastUpdated(building.status.lastUpdated)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className={`btn btn-sm status-btn ${building.status.hasActive ? 'btn-success' : 'btn-outline-secondary'}`}
                                                        onClick={() => navigateToMaintenance(building.key, 'active')}
                                                        disabled={!building.status.hasActive}
                                                    >
                                                        {building.status.hasActive ? (
                                                            <>
                                                                <CheckCircle size={16} className="me-1" />
                                                                Aktiv plan
                                                            </>
                                                        ) : (
                                                            'Plan saknas'
                                                        )}
                                                    </button>
                                                </td>
                                                <td>
                                                    <button
                                                        className={`btn btn-sm status-btn ${building.status.hasProposal ? `btn-${getStatusButtonInfo('proposal', building.status).variant}` : 'btn-outline-secondary'}`}
                                                        onClick={() => navigateToMaintenance(building.key, 'proposal')}
                                                        disabled={!building.status.hasProposal}
                                                    >
                                                        {building.status.hasProposal ? (
                                                            <>
                                                                {getStatusButtonInfo('proposal', building.status).icon}
                                                                {getStatusButtonInfo('proposal', building.status).text}
                                                            </>
                                                        ) : (
                                                            'Inget förslag'
                                                        )}
                                                    </button>
                                                </td>
                                                <td>
                                                    {building.status.hasDraft ? (
                                                        <button
                                                            className="btn btn-sm status-btn btn-info"
                                                            onClick={() => navigateToMaintenance(building.key, 'draft')}
                                                        >
                                                            <FileText size={16} className="me-1" />
                                                            Utkast
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn btn-sm status-btn btn-outline-primary"
                                                            onClick={() => handleCreateDraft(building.key)}
                                                        >
                                                            <FileText size={16} className="me-1" />
                                                            Nytt utkast
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="templates-tab">
                        <MaintenanceTemplatesEditor />
                    </div>
                )}
            </div>

            {/* Create Draft Modal */}
            <div className={`modal fade ${showCreateModal ? 'show' : ''}`} 
                 style={{ display: showCreateModal ? 'block' : 'none' }} 
                 tabIndex={-1} 
                 role="dialog"
                 aria-labelledby="createDraftModalLabel"
                 aria-hidden={!showCreateModal}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="createDraftModalLabel">
                                <FileText size={20} className="me-2" />
                                Skapa underhållsutkast
                            </h5>
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={handleCancelCreateDraft}
                                aria-label="Stäng"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="alert alert-info">
                                <strong>Viktig information:</strong>
                            </div>
                            <p>
                                Utkastet kommer att förifyllas utifrån den inventering och mängdning som finns registrerad på byggnaden. 
                                Skapa utkastet när inventeringen utförd.
                            </p>
                            <p className="mb-0">
                                <strong>Vill du skapa utkastet nu?</strong>
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={handleCancelCreateDraft}
                            >
                                Avbryt
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-primary" 
                                onClick={handleConfirmCreateDraft}
                            >
                                <FileText size={16} className="me-2" />
                                Skapa utkast
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal backdrop */}
            {showCreateModal && (
                <div className="modal-backdrop fade show"></div>
            )}
        </div>
    );
};

export default MaintenanceStart;
